import { supabase } from "./supabase.js";
import type { TableBackgroundKey } from "../config.js";
import type { AnimationSpeed } from "../contexts/CardSetContext.js";

// ============================================================
// Types
// ============================================================

export interface UserPreferences {
  cardSet: string;
  backName: string;
  tableBackground: TableBackgroundKey;
  leftHanded: boolean;
  autoAce: boolean;
  animationSpeed: AnimationSpeed;
}

// ============================================================
// loadPreferences — liest preferred_* aus profiles
// ============================================================

export async function loadPreferences(userId: string): Promise<UserPreferences | null> {
  // Erst Basis-Spalten laden (existieren immer)
  const { data, error } = await supabase
    .from("profiles")
    .select("preferred_card_set, preferred_back, preferred_background, preferred_left_handed")
    .eq("id", userId)
    .single();

  if (error || !data) {
    console.error("[preferences] load failed:", error?.message);
    return null;
  }

  // Neue Spalten separat laden (können fehlen wenn Migration 007 noch nicht gelaufen)
  let autoAce = false;
  let animationSpeed: AnimationSpeed = "normal";
  try {
    const { data: extra } = await supabase
      .from("profiles")
      .select("preferred_auto_ace, preferred_animation_speed")
      .eq("id", userId)
      .single();
    if (extra) {
      autoAce = (extra.preferred_auto_ace as boolean | null) ?? false;
      animationSpeed = ((extra.preferred_animation_speed as string | null) ?? "normal") as AnimationSpeed;
    }
  } catch {
    // Spalten existieren noch nicht — Defaults verwenden
  }

  return {
    cardSet:         (data.preferred_card_set  as string | null) ?? "classic5",
    backName:        (data.preferred_back      as string | null) ?? "blue",
    tableBackground: ((data.preferred_background as string | null) ?? "casinoGreen") as TableBackgroundKey,
    leftHanded:      (data.preferred_left_handed as boolean | null) ?? false,
    autoAce,
    animationSpeed,
  };
}

// ============================================================
// savePreferences — updatet preferred_* in profiles
// ============================================================

export async function savePreferences(
  userId: string,
  prefs: Partial<UserPreferences>
): Promise<void> {
  const payload: Record<string, string | boolean> = {};
  if (prefs.cardSet         !== undefined) payload.preferred_card_set      = prefs.cardSet;
  if (prefs.backName        !== undefined) payload.preferred_back           = prefs.backName;
  if (prefs.tableBackground !== undefined) payload.preferred_background     = prefs.tableBackground;
  if (prefs.leftHanded      !== undefined) payload.preferred_left_handed    = prefs.leftHanded;
  if (prefs.autoAce         !== undefined) payload.preferred_auto_ace        = prefs.autoAce;
  if (prefs.animationSpeed  !== undefined) payload.preferred_animation_speed = prefs.animationSpeed;

  if (Object.keys(payload).length === 0) return;

  const { error } = await supabase
    .from("profiles")
    .update(payload)
    .eq("id", userId);

  if (error) {
    console.error("[preferences] save failed:", error.message, error);
    throw new Error(error.message);
  }
}
